import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { prospectsApi } from '../../lib/api';
import { getScoreColor } from '../../lib/utils';
import { formatLocationCompact } from '../../lib/locations';
import { Search, X, Loader2 } from 'lucide-react';

export const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await prospectsApi.search(query);
        setResults(response.data);
        setIsOpen(true);
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const handleSelect = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const defaultImage = 'https://images.unsplash.com/photo-1508354486999-d33bd3cecc4c?w=100&h=100&fit=crop';

  return (
    <div className="relative" ref={searchRef} data-testid="search-bar">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search prospects..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          className="pl-10 pr-10 h-9 w-48 md:w-64 bg-background border-border text-sm"
          data-testid="search-input"
          aria-label="Search prospects"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
        {query && !loading && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border shadow-lg z-50 max-h-96 overflow-y-auto" data-testid="search-results">
          {results.map((prospect) => (
            <Link
              key={prospect.id}
              to={`/prospects/${prospect.id}`}
              onClick={handleSelect}
              className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors border-b border-border last:border-b-0"
              data-testid={`search-result-${prospect.id}`}
            >
              <div className="w-10 h-10 bg-zinc-900 overflow-hidden flex-shrink-0">
                <img
                  src={prospect.headshot_image_url || defaultImage}
                  alt={prospect.full_name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = defaultImage; }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold uppercase text-sm truncate">
                  {prospect.full_name}
                </p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] font-mono uppercase">
                    {prospect.position}
                  </Badge>
                  {(() => {
                    const compact = formatLocationCompact(prospect, { flag: true });
                    if (!compact) return null;
                    return (
                      <span className="text-xs text-muted-foreground">{compact}</span>
                    );
                  })()}
                </div>
              </div>
              <div className={`font-mono font-bold text-sm`}>
                {prospect.overall_score?.toFixed(1)}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* No Results */}
      {isOpen && query.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border shadow-lg z-50 p-4 text-center">
          <p className="text-muted-foreground text-sm">No prospects found for "{query}"</p>
        </div>
      )}
    </div>
  );
};
